import React, { useState, useCallback, useEffect } from 'react';
import {
  getPlayers, getClasses, getFilters,
  createPlayer, updatePlayer, deletePlayer
} from '../../api/index';
import PlayerEditor from './PlayerEditor';

const AdminDashboard = ({ onLogout, refreshPlayers }) => {
  const [players, setPlayers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [filters, setFilters] = useState({ positions: [], schools: [] });
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [creating, setCreating] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedClass) params.class_year = selectedClass;
      if (search.trim()) params.search = search;
      const [playersData, classesData, filtersData] = await Promise.all([
        getPlayers(params),
        getClasses(),
        getFilters()
      ]);
      setPlayers(playersData);
      setClasses(classesData);
      setFilters(filtersData);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [selectedClass, search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await deletePlayer(id);
      setMessage(`"${name}" deleted.`);
      fetchAll();
      refreshPlayers();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  };

  const handleSave = async (playerData) => {
    try {
      if (playerData.id) {
        await updatePlayer(playerData.id, playerData);
        setMessage(`"${playerData.name}" updated.`);
      } else {
        await createPlayer(playerData);
        setMessage(`"${playerData.name}" created.`);
      }
      setEditingPlayer(null);
      setCreating(false);
      fetchAll();
      refreshPlayers();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      alert('Save failed: ' + err.message);
    }
  };

  if (creating || editingPlayer) {
    return (
      <PlayerEditor
        player={editingPlayer}
        onSave={handleSave}
        onCancel={() => { setEditingPlayer(null); setCreating(false); }}
      />
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h2>Admin Dashboard</h2>
        <button className="btn-add" onClick={() => setCreating(true)}>+ Add Prospect</button>
      </div>

      <div className="admin-toolbar">
        <div className="admin-filters">
          <select
            value={selectedClass || ''}
            onChange={(e) => setSelectedClass(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">All Classes</option>
            {classes.map((y) => (
              <option key={y} value={y}>Class of {y}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Search players..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="admin-meta">
          {message && <span className="msg-success">{message}</span>}
          <span className="player-count">{players.length} prospects</span>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Pos</th>
                <th>School</th>
                <th>Class</th>
                <th>Rating</th>
                <th>HS/COL</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {players.map((p, i) => (
                <tr key={p.id}>
                  <td className="rank-cell">{i + 1}</td>
                  <td className="name-cell">
                    <strong>{p.name}</strong>
                    {p.jersey_number && <span className="jersey">#{p.jersey_number}</span>}
                  </td>
                  <td>{p.position}</td>
                  <td>{p.school || '-'}</td>
                  <td>{p.class_year}</td>
                  <td>
                    <span className="rating-cell">{p.rating > 0 ? p.rating.toFixed(1) : 'NR'}</span>
                  </td>
                  <td>{p.is_high_school ? 'HS' : 'COL'}</td>
                  <td className="actions-cell">
                    <button className="btn-edit" onClick={() => setEditingPlayer(p)}>Edit</button>
                    <button className="btn-del" onClick={() => handleDelete(p.id, p.name)}>Del</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .admin-dashboard {
          max-width: 1400px;
          margin: 0 auto;
          padding: 24px 20px;
        }
        .admin-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        .admin-header h2 { color: #fff; font-size: 1.5rem; }
        .btn-add {
          padding: 10px 24px;
          background: linear-gradient(135deg, #f7971e, #ffd200);
          color: #1a1a2e;
          border: none;
          border-radius: 10px;
          font-weight: 700;
          font-size: 0.9rem;
          cursor: pointer;
        }
        .btn-add:hover { opacity: 0.9; }
        .admin-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 16px;
        }
        .admin-filters {
          display: flex;
          gap: 10px;
        }
        .admin-filters select, .admin-filters input {
          padding: 8px 14px;
          border-radius: 8px;
          border: 1px solid #3a3a5a;
          background: #16162a;
          color: #e0e0e0;
          font-size: 0.85rem;
          outline: none;
        }
        .admin-filters select:focus, .admin-filters input:focus { border-color: #f7971e; }
        .admin-meta { display: flex; gap: 16px; align-items: center; }
        .msg-success {
          background: rgba(76,175,80,0.2);
          color: #4caf50;
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 0.8rem;
        }
        .player-count { color: #8888aa; font-size: 0.85rem; }
        .admin-table-wrap { overflow-x: auto; }
        .admin-table {
          width: 100%;
          border-collapse: collapse;
          background: #1a1a2e;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid #2a2a4a;
        }
        .admin-table th {
          background: #16162a;
          padding: 12px 14px;
          text-align: left;
          color: #8888aa;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          border-bottom: 1px solid #2a2a4a;
        }
        .admin-table td {
          padding: 10px 14px;
          border-bottom: 1px solid #222244;
          font-size: 0.85rem;
          color: #ccc;
        }
        .admin-table tbody tr:hover { background: #22223a; }
        .name-cell { display: flex; align-items: center; gap: 8px; }
        .name-cell strong { color: #fff; }
        .jersey { color: #f7971e; font-size: 0.8rem; font-weight: 600; }
        .rating-cell { color: #ffd200; font-weight: 700; }
        .actions-cell { display: flex; gap: 6px; }
        .btn-edit {
          padding: 5px 12px;
          border-radius: 6px;
          border: 1px solid #f7971e;
          background: transparent;
          color: #f7971e;
          font-size: 0.75rem;
          cursor: pointer;
          font-weight: 600;
        }
        .btn-edit:hover { background: #f7971e; color: #1a1a2e; }
        .btn-del {
          padding: 5px 12px;
          border-radius: 6px;
          border: 1px solid #ff4444;
          background: transparent;
          color: #ff4444;
          font-size: 0.75rem;
          cursor: pointer;
          font-weight: 600;
        }
        .btn-del:hover { background: #ff4444; color: #fff; }
        .loading { text-align: center; color: #8888aa; padding: 60px; }
      `}</style>
    </div>
  );
};

export default AdminDashboard;
